import { FC, useEffect } from 'react';
import type { ElementType } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLoginLink } from '@hooks/useLoginLink';
import { SLOGAN } from '@lib/constants';
import { getIsFreeMember, isUserAuthenticated } from '@lib/utils';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import { captureAnalyticsEventOncePerPage } from '@lib/analytics/once-per-page';
import { BodyRegular, BodyRegularBold, H3 } from '@components/atoms/typography';
import Icon from '@components/atoms/icon';

// FFH-2372: analytics placements for the site-wide paywall card. The gated
// feature is identified by the route pattern — page-level paywalls gate the
// page — so dynamic routes group under one value, matching FFH-2370's
// pageview `route` property.
const PAGE_PAYWALL_PLACEMENT_BY_VARIANT: Record<'modal' | 'embedded', string> =
  {
    modal: 'page_paywall_modal',
    embedded: 'page_paywall_embedded'
  };

type PaywallType = {
  title?: string;
  subtitle?: string;
  returnTo?: string;
  affiliate?: string;
  variant: 'modal' | 'embedded';
  ctaText?: string;
};

const Paywall: FC<PaywallType> = ({
  title = '',
  subtitle = '',
  returnTo,
  affiliate = '',
  variant,
  ctaText = 'Subscribe Now'
}) => {
  const { user } = useAuth0User();
  const loginLink = useLoginLink(returnTo);
  const router = useRouter();

  const isFreeMember = getIsFreeMember(user);

  const placement = PAGE_PAYWALL_PLACEMENT_BY_VARIANT[variant];
  const gatedFeatureRoute = router.pathname;
  // Rewardful's rw.js reads ?via= only when a page loads, so an affiliate
  // join link must be a full page load rather than a client-side navigation,
  // or the contributor is never credited.
  const JoinCta: ElementType = affiliate ? 'a' : Link;

  // FFH-2372: rendering this card IS the paywall view — it only mounts for
  // visitors without access (embedded inline, modal when opened). Once per
  // page visit, not per mount: the card remounts as auth, profile and
  // marketing copy resolve, which shipped 4-6 views per session in prod.
  useEffect(() => {
    captureAnalyticsEventOncePerPage(
      AnalyticsEvents.paywallViewed,
      { placement, feature: gatedFeatureRoute },
      `${placement}|${gatedFeatureRoute}`
    );
  }, [placement, gatedFeatureRoute]);

  return (
    <div
      className={`paywall ${
        variant === 'modal' ? 'min-w-[90vw] md:min-w-[50vw]' : ''
      }`}
      data-cy="paywall-cta"
    >
      <div className="px-3 py-2 flex items-center bg-primary-800 rounded-t-lg paywall-header">
        <Icon name="Premium" className="h-4 w-4 mr-2 text-white" />
        <div className="flex flex-col sm:flex-row">
          <BodyRegularBold className="text-white mb-0">
            Exclusive Content
          </BodyRegularBold>
          <span
            className={`mx-2 text-white mb-0 hidden sm:block text-base font-bold`}
          >
            ·
          </span>
          <BodyRegularBold className="text-white mb-0">
            For members only
          </BodyRegularBold>
        </div>
      </div>
      <div className="overflow-hidden rounded-b-lg shadow bg-primary-100 paywall">
        <div className="p-4">
          <H3 className="mb-1">{title ? `${title}` : SLOGAN}</H3>
          <BodyRegular className="mb-4">
            {subtitle
              ? subtitle
              : isFreeMember
              ? 'Upgrade for access to this tool'
              : 'Join The Hub'}
          </BodyRegular>
          <div className="flex-col sm:flex sm:flex-row space-y-3 sm:space-y-0 items-center justify-between">
            <JoinCta
              href={affiliate ? `/join?via=${affiliate}` : '/join'}
              className="inline-flex outline-0 justify-center text-center rounded-lg transition-colors font-open-sans font-bold text-base border-2 border-transparent min-w-[80px] py-[7px] px-3 text-white bg-primary-600 hover:bg-primary-800 active:bg-neutral-800"
              id="paywall-subscribe-now"
              onClick={() => {
                // FFH-2372: a generic /join CTA points at no specific plan.
                captureAnalyticsEvent(AnalyticsEvents.upgradeCtaClicked, {
                  placement,
                  intended_tier: null
                });
              }}
            >
              {isFreeMember ? 'Upgrade' : ctaText}
            </JoinCta>
            {!isUserAuthenticated(user) && (
              <div className="text-xs">
                Already a member?
                <Link
                  prefetch={false}
                  {...loginLink}
                  className="ml-1 font-bold underline"
                >
                  Log in
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
